import * as React from "react";
import { createPortal } from "react-dom";
import { Building2, User } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Avatar } from "../../../ui/components/Avatar";
import { OrgThumbnail } from "../../../ui/components/OrgThumbnail";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import * as pickerStyles from "../users/AddUserToOrganizationModal.css";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import * as adminUsersApi from "../../lib/api/admin-users";
import type { AdminUserListItem } from "../../lib/api/admin-users";
import * as userApi from "../../lib/api/users";
import * as featureFlagsApi from "../../lib/api/feature-flags";
import type {
  FeatureFlagType,
  FeatureFlagDetail,
  OrgAdminAccess,
  OrgConflictStrategy,
} from "../../lib/api/feature-flags";
import { FeatureFlagValueInput } from "./FeatureFlagValueInput";

export interface FeatureFlagModalProps {
  /** null when creating a new flag */
  flagId: string | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSaved?: () => void;
}

interface FormValues {
  key: string;
  label: string;
  description: string;
  type: FeatureFlagType;
  options: string[];
  value: string;
  orgConflictStrategy: OrgConflictStrategy;
  orgAdminAccess: OrgAdminAccess;
  groupName: string;
  overridable: boolean;
}

const emptyForm = (): FormValues => ({
  key: "",
  label: "",
  description: "",
  type: "boolean",
  options: [],
  value: "false",
  orgConflictStrategy: "most_permissive",
  orgAdminAccess: "hidden",
  groupName: "",
  overridable: true,
});

const ORG_CONFLICT_STRATEGIES: OrgConflictStrategy[] = [
  "most_permissive",
  "most_restrictive",
  "primary_org",
];
const ORG_ADMIN_ACCESS_LEVELS: OrgAdminAccess[] = ["hidden", "read_only", "editable"];

type Translate = ReturnType<typeof useI18n>["t"];

function orgConflictStrategyLabel(t: Translate, strategy: OrgConflictStrategy): string {
  switch (strategy) {
    case "most_permissive":
      return t("pro.system.featureFlags.orgConflictStrategy.mostPermissive");
    case "most_restrictive":
      return t("pro.system.featureFlags.orgConflictStrategy.mostRestrictive");
    case "primary_org":
      return t("pro.system.featureFlags.orgConflictStrategy.primaryOrg");
  }
}

function orgAdminAccessLabel(t: Translate, level: OrgAdminAccess): string {
  switch (level) {
    case "hidden":
      return t("pro.system.featureFlags.orgAdminAccess.hidden");
    case "read_only":
      return t("pro.system.featureFlags.orgAdminAccess.readOnly");
    case "editable":
      return t("pro.system.featureFlags.orgAdminAccess.editable");
  }
}

function OptionsEditor({
  options,
  onChange,
  disabled,
}: {
  options: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = React.useState("");

  const addOption = () => {
    const trimmed = draft.trim();
    if (!trimmed || options.includes(trimmed)) return;
    onChange([...options, trimmed]);
    setDraft("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span className={formStyles.label}>{t("pro.system.featureFlags.form.options")}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((opt) => (
          <span
            key={opt}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              padding: "3px 8px",
              borderRadius: 9999,
              background: "var(--color-muted)",
              border: "1px solid var(--color-border)",
            }}
          >
            {opt}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(options.filter((o) => o !== opt))}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#9CA3AF", lineHeight: 1 }}
                aria-label={t("pro.system.featureFlags.removeOption", { opt })}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div style={{ display: "flex", gap: 8 }}>
          <TextInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("pro.system.featureFlags.optionPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOption();
              }
            }}
          />
          <Button type="button" color="neutral" onClick={addOption} disabled={!draft.trim()}>
            {t("pro.system.featureFlags.add")}
          </Button>
        </div>
      )}
    </div>
  );
}

type OverrideTarget = "user" | "organization";

type SearchResult =
  | { kind: "user"; user: AdminUserListItem }
  | { kind: "organization"; organization: userApi.Organization };

function searchResultKey(r: SearchResult): string {
  return r.kind === "user" ? `u:${r.user.id}` : `o:${r.organization.id}`;
}

function searchResultLabel(r: SearchResult): string {
  return r.kind === "user"
    ? `${r.user.firstName} ${r.user.lastName} (${r.user.email})`
    : r.organization.name;
}

const overrideCardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  padding: "6px 10px",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
};

function OverrideCard({
  media,
  title,
  subtitle,
  value,
  onRemove,
}: {
  media: React.ReactNode;
  title: string;
  subtitle?: string;
  value: string;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  return (
    <div style={overrideCardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {media}
        <div style={{ minWidth: 0 }}>
          <Text level="body-medium" weight="bold">
            {title}
          </Text>
          <Text level="body-small" style={{ color: "var(--color-fgSecondary)" }}>
            {subtitle} {t("pro.system.featureFlags.overrides.valueLabel")}{" "}
            <span style={{ fontFamily: "monospace" }}>{value}</span>
          </Text>
        </div>
      </div>
      <Button type="button" color="neutral" variant="outline" size="sm" onClick={onRemove}>
        {t("shared.media.remove")}
      </Button>
    </div>
  );
}

function OverridesSection({
  flagId,
  flagType,
  flagOptions,
  token,
}: {
  flagId: string;
  flagType: FeatureFlagType;
  flagOptions: string[];
  token: string;
}) {
  const { t } = useI18n();
  const [detail, setDetail] = React.useState<FeatureFlagDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [target, setTarget] = React.useState<OverrideTarget>("user");
  const [search, setSearch] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);
  const [selected, setSelected] = React.useState<SearchResult | null>(null);
  const [allOrganizations, setAllOrganizations] = React.useState<userApi.Organization[] | null>(null);
  const [overrideValue, setOverrideValue] = React.useState(flagType === "boolean" ? "true" : "");
  const [saving, setSaving] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const dropdownOpen = !selected && searchResults.length > 0;

  const updateDropdownPosition = React.useCallback(() => {
    const trigger = searchInputRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  React.useEffect(() => {
    if (!dropdownOpen) {
      setDropdownPosition(null);
      return;
    }
    const run = () => requestAnimationFrame(updateDropdownPosition);
    run();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [dropdownOpen, updateDropdownPosition]);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const eventTarget = event.target as Node;
      if (
        dropdownRef.current?.contains(eventTarget) ||
        searchInputRef.current?.contains(eventTarget)
      ) {
        return;
      }
      setSearchResults([]);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [dropdownOpen]);

  const load = React.useCallback(() => {
    setLoading(true);
    featureFlagsApi
      .fetchFeatureFlag(token, flagId)
      .then(setDetail)
      .catch((err) => setError(err?.message || t("pro.system.featureFlags.error.loadOverrides")))
      .finally(() => setLoading(false));
  }, [token, flagId, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (target !== "organization" || allOrganizations) return;
    void userApi
      .fetchOrganizations(token)
      .then(setAllOrganizations)
      .catch(() => setAllOrganizations([]));
  }, [target, allOrganizations, token]);

  React.useEffect(() => {
    const query = search.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }
    if (target === "organization") {
      const lower = query.toLowerCase();
      const overridden = new Set(detail?.organizationOverrides.map((o) => o.organizationId));
      setSearchResults(
        (allOrganizations ?? [])
          .filter((o) => !overridden.has(o.id) && o.name.toLowerCase().includes(lower))
          .slice(0, 8)
          .map((organization) => ({ kind: "organization", organization })),
      );
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void adminUsersApi
        .fetchUsersList(token, { page: 1, limit: 8, search: query })
        .then((res) => {
          if (!cancelled) setSearchResults(res.data.map((user) => ({ kind: "user", user })));
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, token, target, allOrganizations, detail]);

  const resetAddForm = () => {
    setSelected(null);
    setSearch("");
    setSearchResults([]);
    setOverrideValue(flagType === "boolean" ? "true" : "");
  };

  const handleTargetChange = (next: string) => {
    setTarget(next as OverrideTarget);
    resetAddForm();
  };

  const handleAddOverride = async () => {
    if (!selected || !overrideValue) return;
    setSaving(true);
    setError(null);
    try {
      if (selected.kind === "user") {
        await featureFlagsApi.upsertFeatureFlagOverride(token, flagId, selected.user.id, overrideValue);
      } else {
        await featureFlagsApi.upsertOrganizationFeatureFlagOverride(
          token,
          flagId,
          selected.organization.id,
          overrideValue,
        );
      }
      resetAddForm();
      load();
    } catch (err: any) {
      setError(err?.message || t("pro.system.featureFlags.error.addOverride"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (remove: () => Promise<void>) => {
    setError(null);
    try {
      await remove();
      load();
    } catch (err: any) {
      setError(err?.message || t("pro.system.featureFlags.error.removeOverride"));
    }
  };

  const hasOverrides =
    !!detail && (detail.overrides.length > 0 || detail.organizationOverrides.length > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
      <Text level="h3" weight="bold">
        {t("pro.system.featureFlags.overrides.title")}
      </Text>

      {error && <Callout variant="error">{error}</Callout>}
      {loading && (
        <Text level="body-medium" style={{ color: "var(--color-fgMuted)" }}>
          {t("shared.common.loading")}
        </Text>
      )}

      {!loading && detail && !hasOverrides && (
        <Text level="body-medium" style={{ color: "var(--color-fgMuted)" }}>
          {t("pro.system.featureFlags.overrides.none")}
        </Text>
      )}

      {!loading && detail && hasOverrides && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {detail.organizationOverrides.map((override) => (
            <OverrideCard
              key={override.id}
              media={
                <OrgThumbnail
                  name={override.organization?.name ?? override.organizationId}
                  logoUrl={override.organization?.logoUrl}
                  size={32}
                />
              }
              title={override.organization?.name ?? override.organizationId}
              subtitle={t("pro.system.featureFlags.overrides.organizationLabel")}
              value={override.value}
              onRemove={() =>
                void handleRemove(() =>
                  featureFlagsApi.deleteOrganizationFeatureFlagOverride(
                    token,
                    flagId,
                    override.organizationId,
                  ),
                )
              }
            />
          ))}
          {detail.overrides.map((override) => (
            <OverrideCard
              key={override.id}
              media={
                <Avatar
                  email={override.user?.email ?? ""}
                  firstName={override.user?.firstName ?? ""}
                  lastName={override.user?.lastName ?? ""}
                  image={override.user?.avatarUrl ?? override.user?.googleAvatarUrl ?? undefined}
                  size={32}
                />
              }
              title={
                override.user
                  ? `${override.user.firstName} ${override.user.lastName}`
                  : override.userId
              }
              subtitle={override.user?.email}
              value={override.value}
              onRemove={() =>
                void handleRemove(() =>
                  featureFlagsApi.deleteFeatureFlagOverride(token, flagId, override.userId),
                )
              }
            />
          ))}
        </div>
      )}

      <div
        style={{
          borderTop: "1px solid var(--color-border)",
          paddingTop: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span className={formStyles.label}>{t("pro.system.featureFlags.overrides.add")}</span>
          <TabSwitcher
            aria-label={t("pro.system.featureFlags.overrides.targetLabel")}
            value={target}
            onChange={handleTargetChange}
            tabs={[
              { value: "user", label: t("pro.system.featureFlags.overrides.targetUsers"), icon: <User size={14} /> },
              {
                value: "organization",
                label: t("pro.system.featureFlags.overrides.targetOrganizations"),
                icon: <Building2 size={14} />,
              },
            ]}
          />
        </div>
        <TextInput
          ref={searchInputRef}
          value={selected ? searchResultLabel(selected) : search}
          onChange={(e) => {
            setSelected(null);
            setSearch(e.target.value);
          }}
          placeholder={
            target === "user"
              ? t("pro.system.featureFlags.overrides.searchUsers")
              : t("pro.system.featureFlags.overrides.searchOrganizations")
          }
          disabled={saving}
        />
        {dropdownOpen &&
          dropdownPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className={pickerStyles.pickerDropdown}
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
              role="listbox"
              aria-label={t("pro.system.featureFlags.aria.searchResults")}
            >
              {searchResults.map((result) => (
                <button
                  key={searchResultKey(result)}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className={pickerStyles.pickerOption}
                  onClick={() => {
                    setSelected(result);
                    setSearchResults([]);
                  }}
                >
                  {result.kind === "user" ? (
                    <>
                      <Avatar
                        email={result.user.email}
                        firstName={result.user.firstName}
                        lastName={result.user.lastName}
                        image={result.user.avatarUrl ?? undefined}
                        size={24}
                      />
                      <span className={pickerStyles.orgName}>
                        {result.user.firstName} {result.user.lastName} — {result.user.email}
                      </span>
                    </>
                  ) : (
                    <>
                      <OrgThumbnail
                        name={result.organization.name}
                        logoUrl={result.organization.logoUrl}
                        size={24}
                      />
                      <span className={pickerStyles.orgName}>{result.organization.name}</span>
                    </>
                  )}
                </button>
              ))}
            </div>,
            document.body,
          )}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <FeatureFlagValueInput type={flagType} options={flagOptions} value={overrideValue} onChange={setOverrideValue} disabled={saving} />
          </div>
          <Button
            type="button"
            color="primary"
            onClick={() => void handleAddOverride()}
            disabled={saving || !selected || !overrideValue}
          >
            {saving ? t("pro.system.featureFlags.saving") : t("pro.system.featureFlags.overrides.add")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FeatureFlagModal({ flagId, open, setOpen, onSaved }: FeatureFlagModalProps) {
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? localStorage.getItem("access_token");
  const isCreate = flagId === null;

  const [values, setValues] = React.useState<FormValues>(emptyForm());
  const [loading, setLoading] = React.useState(!isCreate);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setValues(emptyForm());
      setLoading(false);
      setError(null);
      return;
    }
    if (!token || !flagId) return;
    setLoading(true);
    featureFlagsApi
      .fetchFeatureFlag(token, flagId)
      .then((flag) =>
        setValues({
          key: flag.key,
          label: flag.label,
          description: flag.description ?? "",
          type: flag.type,
          options: flag.options ?? [],
          value: flag.value,
          orgConflictStrategy: flag.orgConflictStrategy ?? "most_permissive",
          orgAdminAccess: flag.orgAdminAccess ?? "hidden",
          groupName: flag.groupName ?? "",
          overridable: flag.overridable,
        }),
      )
      .catch((err) => setError(err?.message || t("pro.system.featureFlags.error.loadFlag")))
      .finally(() => setLoading(false));
  }, [open, isCreate, token, flagId, t]);

  const patch = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleTypeChange = (nextType: FeatureFlagType) => {
    setValues((prev) => ({
      ...prev,
      type: nextType,
      value: nextType === "boolean" ? "true" : nextType === "enum" ? "" : "",
      options: nextType === "enum" ? prev.options : [],
    }));
  };

  const canSubmit =
    values.key.trim().length > 0 &&
    values.label.trim().length > 0 &&
    (values.type !== "enum" || values.options.length > 0) &&
    values.value.trim().length >= 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        await featureFlagsApi.createFeatureFlag(token, {
          key: values.key.trim(),
          label: values.label.trim(),
          description: values.description.trim() || undefined,
          type: values.type,
          options: values.type === "enum" ? values.options : undefined,
          value: values.value,
          orgConflictStrategy: values.orgConflictStrategy,
          orgAdminAccess: values.orgAdminAccess,
          groupName: values.groupName.trim() || null,
          overridable: values.overridable,
        });
      } else if (flagId) {
        await featureFlagsApi.updateFeatureFlag(token, flagId, {
          label: values.label.trim(),
          description: values.description.trim() || null,
          type: values.type,
          options: values.type === "enum" ? values.options : null,
          value: values.value,
          orgConflictStrategy: values.orgConflictStrategy,
          orgAdminAccess: values.orgAdminAccess,
          groupName: values.groupName.trim() || null,
          overridable: values.overridable,
        });
      }
      onSaved?.();
    } catch (err: any) {
      setError(err?.message || t("pro.system.featureFlags.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !flagId) return;
    setDeleting(true);
    setError(null);
    try {
      await featureFlagsApi.deleteFeatureFlag(token, flagId);
      onSaved?.();
    } catch (err: any) {
      setError(err?.message || t("pro.system.featureFlags.error.delete"));
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="520px" maxWidth="560px" closeOnClickOff>
      {loading ? (
        <Modal.Body>
          <Text level="body-medium" style={{ color: "var(--color-fgMuted)" }}>
            {t("shared.common.loading")}
          </Text>
        </Modal.Body>
      ) : (
        <Modal.Form onSubmit={handleSubmit}>
          <Modal.Header>
            <Text level="h2" weight="bold">
              {isCreate ? t("pro.system.featureFlags.modal.create") : t("pro.system.featureFlags.modal.edit")}
            </Text>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <TextInput
                label={t("pro.system.featureFlags.form.keyRequired")}
                value={values.key}
                onChange={(e) => patch("key", e.target.value)}
                placeholder={t("pro.system.featureFlags.form.keyPlaceholder")}
                disabled={saving || !isCreate}
                autoFocus={isCreate}
              />
              <TextInput
                label={t("pro.system.featureFlags.form.labelRequired")}
                value={values.label}
                onChange={(e) => patch("label", e.target.value)}
                placeholder={t("pro.system.featureFlags.form.labelPlaceholder")}
                disabled={saving}
              />
              <TextInput
                label={t("pro.system.featureFlags.form.description")}
                value={values.description}
                onChange={(e) => patch("description", e.target.value)}
                placeholder={t("pro.system.featureFlags.form.descriptionPlaceholder")}
                disabled={saving}
              />
              <TextInput
                label={t("pro.system.featureFlags.form.group")}
                value={values.groupName}
                onChange={(e) => patch("groupName", e.target.value)}
                placeholder={t("pro.system.featureFlags.form.groupPlaceholder")}
                disabled={saving}
              />

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label className={formStyles.label}>{t("pro.system.featureFlags.form.typeRequired")}</label>
                <select
                  className={formStyles.input}
                  value={values.type}
                  onChange={(e) => handleTypeChange(e.target.value as FeatureFlagType)}
                  disabled={saving}
                >
                  <option value="boolean">{t("pro.system.featureFlags.type.boolean")}</option>
                  <option value="string">{t("pro.system.featureFlags.type.string")}</option>
                  <option value="number">{t("pro.system.featureFlags.type.number")}</option>
                  <option value="enum">{t("pro.system.featureFlags.type.enum")}</option>
                </select>
              </div>

              {values.type === "enum" && (
                <OptionsEditor
                  options={values.options}
                  onChange={(next) => patch("options", next)}
                  disabled={saving}
                />
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label className={formStyles.label}>{t("pro.system.featureFlags.form.globalValue")}</label>
                <FeatureFlagValueInput
                  type={values.type}
                  options={values.options}
                  value={values.value}
                  onChange={(v) => patch("value", v)}
                  disabled={saving}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label className={formStyles.label}>{t("pro.system.featureFlags.form.scope")}</label>
                <select
                  className={formStyles.input}
                  value={values.overridable ? "overridable" : "global"}
                  onChange={(e) => patch("overridable", e.target.value === "overridable")}
                  disabled={saving}
                >
                  <option value="overridable">{t("pro.system.featureFlags.scope.overridable")}</option>
                  <option value="global">{t("pro.system.featureFlags.scope.global")}</option>
                </select>
              </div>

              {values.overridable && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label className={formStyles.label}>{t("pro.system.featureFlags.form.orgConflictStrategy")}</label>
                    <select
                      className={formStyles.input}
                      value={values.orgConflictStrategy}
                      onChange={(e) => patch("orgConflictStrategy", e.target.value as OrgConflictStrategy)}
                      disabled={saving}
                    >
                      {ORG_CONFLICT_STRATEGIES.map((strategy) => (
                        <option key={strategy} value={strategy}>
                          {orgConflictStrategyLabel(t, strategy)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label className={formStyles.label}>{t("pro.system.featureFlags.form.orgAdminAccess")}</label>
                    <select
                      className={formStyles.input}
                      value={values.orgAdminAccess}
                      onChange={(e) => patch("orgAdminAccess", e.target.value as OrgAdminAccess)}
                      disabled={saving}
                    >
                      {ORG_ADMIN_ACCESS_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {orgAdminAccessLabel(t, level)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {error && <Callout variant="error">{error}</Callout>}

              {!isCreate && flagId && token && values.overridable && (
                <OverridesSection flagId={flagId} flagType={values.type} flagOptions={values.options} token={token} />
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ justifyContent: "space-between" }}>
            {!isCreate ? (
              <Button
                type="button"
                color="danger"
                variant="outline"
                onClick={() => void handleDelete()}
                disabled={saving || deleting}
              >
                {deleting ? t("pro.system.featureFlags.deleting") : t("shared.common.delete")}
              </Button>
            ) : (
              <span />
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <Button type="button" color="neutral" onClick={() => setOpen(false)} disabled={saving || deleting}>
                {t("shared.common.cancel")}
              </Button>
              <Button type="submit" color="primary" disabled={saving || deleting || !canSubmit}>
                {saving
                  ? t("pro.system.featureFlags.saving")
                  : isCreate
                    ? "Create feature"
                    : t("pro.system.featureFlags.saveChanges")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Form>
      )}
    </Modal>
  );
}
